import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { createFileRoute } from '@tanstack/react-router';
import { BrandDetails } from '~/components/common/brand/brand-details';
import { Button } from '~/components/ui/button';
import { Illustration } from '~/components/ui/illustration/illustration';
import { Loader } from '~/components/ui/loader';
import { SaleAvatarGroup } from '~/components/ui/sale-avatar-group';
import {
  Sidebar,
  SidebarContent,
  SidebarTrigger,
} from '~/components/ui/sidebar';
import { toast } from '~/components/ui/toast';
import { useBrandQuery } from '~/graphql/graphql.gen';
import { useCopyToClipboard } from '~/hooks/use-copy-to-clipboard';
import { withAuthorizationRequired } from '~/libs/auth';
import { AppLoader } from '~/routes/_auth';
import { titleWrapper } from '~/utils/title';
import { brandQueryParamsSchema } from '~/validators/brand-validator';

export const BRAND_SCOPES = [
  'scope.meusnidus.sales.view',
  'scope.meusnidus.sales.write',
];

export const Route = createFileRoute('/_auth/_main-navigation/marque/$brandId')(
  {
    component: withAuthorizationRequired(RouteComponent, {
      onRedirecting: AppLoader,
      requireScope: BRAND_SCOPES,
    }),
    validateSearch: (search) => brandQueryParamsSchema.parse(search),
  },
);

function RouteComponent() {
  const { brandId } = Route.useParams();
  const navigate = Route.useNavigate();
  const params = Route.useSearch();

  const { data: brand, isLoading } = useBrandQuery(
    { brandId: brandId },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: params.edit !== true,
      refetchOnReconnect: params.edit !== true,
      retry: false,
      select: (result) => result.brand,
    },
  );

  const [brandIdClipboardState, brandIdToClipboard] = useCopyToClipboard();
  const [brandUrlClipboardState, brandUrlToClipboard] = useCopyToClipboard();
  const { t } = useTranslation();

  useEffect(() => {
    const { value, error } = brandIdClipboardState;
    if (value) {
      toast.success(t('brand_page.copy_brand_id.success'));
    }
    if (error) {
      toast.error(t('brand_page.copy_brand_id.error'));
    }
  }, [brandIdClipboardState, t]);

  useEffect(() => {
    const { value, error } = brandUrlClipboardState;
    if (value) {
      toast.success(t('brand_page.copy_brand_url.success'));
    }
    if (error) {
      toast.error(t('brand_page.copy_brand_url.error'));
    }
  }, [brandUrlClipboardState, t]);

  if (isLoading) {
    return (
      <>
        <title>{titleWrapper(t('brand_page.loading'))}</title>
        <section className="relative">
          <div className="h-screen space-y-4 p-6 sm:p-14">
            <div className="flex items-center justify-center">
              <Loader className="mt-2 size-8" />
            </div>
          </div>
        </section>
      </>
    );
  }

  if (!brand) {
    return (
      <>
        <title>{titleWrapper(t('not_found.title'))}</title>
        <section className="relative">
          <div className="h-screen space-y-4 p-6 sm:p-14">
            <div className="flex h-full flex-col items-center justify-center">
              <Illustration illustration="boxEmpty" />
              <h3 className="mt-6 text-3.5xl font-bold">
                {t('brand_page.brand_not_found')}
              </h3>
            </div>
          </div>
        </section>
      </>
    );
  }

  const isBrandDetailsOpen = params.edit === true;

  return (
    <>
      <title>{titleWrapper(brand.name ?? '')}</title>
      <section className="relative">
        <div className="space-y-4 p-6 sm:p-14">
          <header className="mx-auto max-w-6xl space-y-4">
            <div className="flex flex-wrap justify-between">
              <h1 className="text-3xl font-bold text-gray-900">{brand.name}</h1>
              <div className="flex items-center space-x-4">
                <SaleAvatarGroup
                  saleName={brand.contactName ?? ''}
                  logisticsName=""
                  setupName=""
                />
                <button
                  type="button"
                  onClick={() => brandIdToClipboard(brand.id)}
                  className="text-sm font-semibold text-green-900"
                >
                  {t('brand_page.copy_brand_id_label')}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    brandUrlToClipboard(
                      `https://mychoose.appchoose.io/${brand.id}`,
                    )
                  }
                  className="text-sm font-semibold text-green-900"
                >
                  {t('brand_page.copy_brand_url_label')}
                </button>
                <Button
                  type="button"
                  onClick={() => {
                    navigate({
                      search: (prev) => ({
                        ...prev,
                        edit: true,
                      }),
                    });
                  }}
                >
                  {t('brand_page.edit_brand')}
                </Button>
              </div>
            </div>
          </header>
          <div className="mx-auto max-w-6xl rounded-2xl border border-gray-100 py-12">
            <header className="mx-8 mb-8 flex flex-wrap items-center justify-between border-b border-gray-100 pb-7 md:mx-10">
              <h2 className="space-x-1 text-2xl font-bold text-gray-900">
                {t('brand_page.information')}
              </h2>
            </header>
            <div className="mx-8 md:mx-10">
              <dl className="space-y-6">
                <div className="space-y-2">
                  <dt className="flex items-center text-xs font-semibold uppercase tracking-wider text-gray-700">
                    {t('brand_page.instagram')}
                  </dt>
                  <dd>
                    <a
                      href={`https://instagram.com/${brand.instagram}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-green-900"
                    >
                      @{brand.instagram}
                    </a>
                  </dd>
                </div>
                <div className="space-y-2">
                  <dt className="flex items-center text-xs font-semibold uppercase tracking-wider text-gray-700">
                    {t('brand_page.description')}
                  </dt>
                  <dd className="whitespace-pre-line">{brand.about}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
        <Sidebar
          open={isBrandDetailsOpen}
          onOpenChange={() => {
            navigate({
              search: (prev) => ({
                ...prev,
                edit: false,
              }),
            });
          }}
          modal={false}
        >
          {/* Sidebar overlay when modal={false} */}
          {isBrandDetailsOpen ? (
            <SidebarTrigger
              className="fixed inset-0 z-40 bg-black/30 data-[state=closed]:duration-300 data-[state=open]:duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              data-state={isBrandDetailsOpen ? 'open' : 'closed'}
            />
          ) : null}
          <SidebarContent
            className="lg:w-[50rem]"
            // Disable overflow on body when the sidebar is open
            data-no-modal={true}
            // Prevent the sidebar from closing when clicking outside (e.g. on a Chrome extension)
            onPointerDownOutside={(e) => e.preventDefault()}
            onFocusOutside={(e) => e.preventDefault()}
          >
            <BrandDetails brand={brand} />
          </SidebarContent>
        </Sidebar>
      </section>
    </>
  );
}
